import { useState, type ChangeEvent } from "react";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type {
  CaseloadWork,
  ExtendedThalamosUser,
} from "@thalamos/aspire-common";
import { isCqcUserSession, isSoadUserSession } from "@thalamos/aspire-common";

import { generateSoadFilename, StatusTableCells } from "./StatusTableCells.js";
import type { WorkMode } from "./types.js";
import { api } from "~/api.js";
import { Button, ButtonIcon } from "~/components/design-system/index.js";

export default function RenderedTable({
  work,
  showNhsNumber,
  user,
  mode,
}: {
  work: CaseloadWork;
  showNhsNumber: boolean;
  user: ExtendedThalamosUser;
  mode: WorkMode;
}) {
  const { t } = useTranslation();
  const cellStyles = { py: "10px", fontWeight: "bold" };

  const isSoad = isSoadUserSession(user);
  const isCqc = isCqcUserSession(user);
  const isSoadOrCqc = isSoad || isCqc;

  const [selectedItems, setSelectedItems] = useState<
    { id: string; formContextId: string }[]
  >([]);

  const [isDownloading, setIsDownloading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info"
  >("info");

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allItems = work.work.map((item) => ({
        id: item.id,
        formContextId: item.formContextId,
      }));
      setSelectedItems(allItems);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectOne = (id: string, formContextId: string) => {
    setSelectedItems((prev) => {
      const exists = prev.some((item) => item.formContextId === formContextId);
      return exists
        ? prev.filter((item) => item.formContextId !== formContextId)
        : [...prev, { id, formContextId }];
    });
  };

  const isAllSelected =
    work?.work?.length > 0 && selectedItems.length === work.work.length;

  const pollForDownload = async (
    jobId: string,
    retries = 0,
    onComplete: () => Promise<void>,
  ) => {
    const pollInterval = 2000;
    const maxRetries = 30;

    try {
      const poll = await api.bulkDownload.poll(jobId);
      if (poll.data.status === "completed" && poll.data.downloadUrl) {
        setIsDownloading(false);
        setSnackbarMessage("Download ready!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        window.open(poll.data.downloadUrl, "_blank");

        await onComplete();
      } else if (poll.data.status === "failed") {
        setIsDownloading(false);
        setSnackbarMessage("Download failed");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else if (retries < maxRetries) {
        setTimeout(
          () => pollForDownload(jobId, retries + 1, onComplete),
          pollInterval,
        );
      } else {
        setIsDownloading(false);
        setSnackbarMessage("Timed out waiting for download");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch {
      setIsDownloading(false);
      setSnackbarMessage("Failed to check download status");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDownloadSelected = async () => {
    try {
      setIsDownloading(true);
      setSnackbarMessage("Preparing download...");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);

      const formContextIds = selectedItems.map((item) => item.formContextId);

      const selectedWorkItems = work.work.filter((item) =>
        formContextIds.includes(item.formContextId),
      );

      const filesToAdd = selectedWorkItems.map((item) => {
        return {
          fileName: generateSoadFilename(item),
          formContextId: item.formContextId,
        };
      });

      const response = await api.bulkDownload.create(filesToAdd);
      const jobId = response.data.downloadId;

      await pollForDownload(jobId, 0, async () => {
        try {
          const itemIds = selectedItems.map((item) => item.id);
          await api.work.bulkArchive(itemIds);
          setSnackbarMessage(
            `Work items${mode === "archive" ? "" : " archived and"} downloaded successfully`,
          );
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          setSelectedItems([]); // Clear selection after archive
        } catch {
          setSnackbarMessage("Failed to archive and download some work items");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      });
    } catch (err: any) {
      setIsDownloading(false);
      setSnackbarMessage(
        err?.response?.data?.reason ??
          "Unexpected error occurred while downloading",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => {
            setSnackbarOpen(false);
          }}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <TableContainer sx={{ mb: 2, overflowX: "auto" }} component={Paper}>
        {isCqc && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
            }}
          >
            <Button
              label={isDownloading ? "Downloading…" : "Download Selected"}
              size="small"
              disabled={selectedItems.length === 0 || isDownloading}
              onClick={() => {
                void handleDownloadSelected();
              }}
              variant="contained"
              customStartIcon={
                isDownloading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : undefined
              }
              startIcon={!isDownloading ? ButtonIcon.download : undefined}
            />
          </Box>
        )}
        <Table
          key={work.count}
          sx={{ minWidth: 850 }}
          aria-label="caseload table"
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: "common.lightGray" }}>
              <TableCell align="left" sx={{ ...cellStyles }}>
                {t("pages.caseload.table.patientName")}
              </TableCell>
              <TableCell align="left" sx={{ ...cellStyles }}>
                {t("pages.caseload.table.dob")}
              </TableCell>
              {showNhsNumber && (
                <TableCell align="left" sx={{ ...cellStyles }}>
                  {t("pages.caseload.table.nhsNumber")}
                </TableCell>
              )}
              {!isSoadOrCqc && (
                <TableCell align="left" sx={{ ...cellStyles }}>
                  {t("pages.caseload.table.form")}
                </TableCell>
              )}
              {isSoadOrCqc && (
                <TableCell align="left" sx={{ ...cellStyles }}>
                  {t("pages.caseload.table.requestId")}
                </TableCell>
              )}
              {!isSoadOrCqc && (
                <TableCell align="left" sx={{ ...cellStyles }}>
                  {t("pages.caseload.table.summary")}
                </TableCell>
              )}
              {!isSoadOrCqc && (
                <TableCell align="left" sx={{ ...cellStyles }}>
                  {t("pages.caseload.table.claimed")}
                </TableCell>
              )}
              {isSoadOrCqc && (
                <TableCell align="left" sx={{ ...cellStyles }}>
                  {t("pages.caseload.table.caseStatus")}
                </TableCell>
              )}
              {isCqc && (
                <TableCell align="left" sx={{ ...cellStyles }}>
                  {t("pages.caseload.table.soad")}
                </TableCell>
              )}
              {isCqc && (
                <TableCell align="left" sx={{ ...cellStyles }}>
                  {t("pages.caseload.table.submittedTo")}
                </TableCell>
              )}
              {isCqc && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              <TableCell align="left" sx={{ ...cellStyles }}>
                {t("pages.caseload.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          {work?.work?.map((workItem) => (
            <StatusTableCells
              key={workItem.formContextId}
              item={workItem}
              showNhsNumber={showNhsNumber}
              isSelected={selectedItems.some(
                (selected) => selected.formContextId === workItem.formContextId,
              )}
              onSelect={() => {
                handleSelectOne(workItem.id, workItem.formContextId);
              }}
            />
          ))}
        </Table>
      </TableContainer>
    </>
  );
}
